<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <div class="mb-5">
          <label class="label-input">Media</label>
          <div class="group-item">
            <v-radio-group v-model="mediaType" row hide-details class="mt-0 mb-3">
              <v-radio label="Video" value="video" />
              <v-radio label="Image" value="image" />
            </v-radio-group>
            <template v-if="mediaType === 'video'">
              <v-text-field v-model="fields.media.videoSrc" hide-details class="mb-3" />
              <VideoPreview :value="fields.media.videoSrc" />
            </template>
            <UploadImage
              v-else
              :image="{ imageSrc: fields.media.image }"
              :height="`260`"
              :typeUpload="'image'"
              @callback="obj => (fields.media.image = obj.imageSrc)"
              @callbackDelete="fields.media.image = ''"
            />
            <v-row class="mt-1">
              <v-col cols="12" md="6">
                <label>Order:</label>
                <v-radio-group v-model.number="mediaOrder" row hide-details class="mt-0">
                  <v-radio small label="Left" :value="1" />
                  <v-radio small label="Right" :value="2" />
                </v-radio-group>
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox v-model="fields.media.useCustomWidth" label="Use custom width" hide-details class="mt-0" />
                <div>
                  <v-text-field
                    v-if="fields.media.useCustomWidth"
                    v-model.number="fields.media.customWidth"
                    type="number"
                    hide-details
                    class="mt-2"
                    suffix="px"
                  />
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Content</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12">
                <ColorPicker v-model="fields.content.bgColor" label="Background color" />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Heading</label>
            <v-checkbox v-model="fields.heading.show" label="Show" hide-details class="mt-0" />
          </div>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.heading.content" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Stats (max {{ MAX_STATS_ITEMS }})</label>
            <v-btn
              small
              color="primary"
              :disabled="fields.stats.items.length >= MAX_STATS_ITEMS"
              @click="handleAddStatsItem"
            >
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Stat</span>
            </v-btn>
          </div>
          <div class="group-item mb-3">
            <v-row>
              <v-col cols="12" md="4">
                <v-checkbox v-model="fields.stats.show" label="Show stats" hide-details class="mt-0" />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker v-model="fields.stats.bgColor" label="Background color" />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker v-model="fields.stats.borderColor" label="Border color" />
              </v-col>
            </v-row>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.stats.items" :key="`stats-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>Item {{ index + 1 }}</span>
                    <v-btn
                      icon
                      :disabled="fields.stats.items.length <= MIN_STATS_ITEMS"
                      @click.stop="handleRemoveStatsItem(index)"
                    >
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Pills </label>
            <v-btn small color="primary" @click="handleAddPillItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Pill</span>
            </v-btn>
          </div>
          <div class="group-item mb-3">
            <v-row>
              <v-col cols="12" md="4">
                <v-checkbox v-model="fields.pills.show" label="Show pills" hide-details class="mt-0" />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker v-model="fields.pills.bgColor" label="Background color" />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker v-model="fields.pills.borderColor" label="Border color" />
              </v-col>
            </v-row>
          </div>
          <div class="group-item">
            <div
              v-for="(item, index) in fields.pills.items"
              :key="`pill-item-${index}`"
              class="d-flex align-center mb-3"
              style="gap: 10px"
            >
              <v-text-field v-model="item.text" hide-details />
              <v-btn icon :disabled="fields.pills.items.length <= MIN_PILL_ITEMS" @click="handleRemovePillItem(index)">
                <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Feature Callout</label>
            <v-checkbox v-model="fields.featureCallout.show" label="Show" hide-details class="mt-0" />
          </div>
          <div class="group-item">
            <ColorPicker v-model="fields.featureCallout.colorLine" label="Line color" class="mb-3" />
            <ckeditor :editor="editor" v-model="fields.featureCallout.content" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Testimonial</label>
            <v-checkbox v-model="fields.testimonial.show" label="Show" hide-details class="mt-0" />
          </div>
          <div class="group-item">
            <ColorPicker v-model="fields.testimonial.colorHighlight" label="Highlight color" class="mb-3" />
            <label class="mb-2 d-block">Avatar</label>
            <UploadImage
              :image="{ imageSrc: fields.testimonial.avatar }"
              :height="`160`"
              :typeUpload="'image'"
              @callback="obj => (fields.testimonial.avatar = obj.imageSrc)"
              @callbackDelete="fields.testimonial.avatar = ''"
            />
            <ckeditor class="mt-3" :editor="editor" v-model="fields.testimonial.content" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Button</label>
          <ButtonPrimary :button="fields.button" />
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import ButtonPrimary from '../common/ui-modules/button-primary/editor.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';
import ColorPicker from '../common/ui-modules/inputs/color-picker.vue';
import VideoPreview from '../common/ui-modules/inputs/video-preview.vue';

const MAX_STATS_ITEMS = 3;
const MIN_STATS_ITEMS = 2;
const MAX_PILL_ITEMS = 5;
const MIN_PILL_ITEMS = 2;

const buildStatsItemDefault = () => ({ content: '<h3></h3><p></p>' });
const buildPillItemDefault = () => ({ text: '' });

const getDefaultFields = () => ({
  title: 'Calyrafit Side By Side Section',
  settings: {
    paddingTop: true,
    paddingBottom: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: true,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  media: {
    useVideo: true,
    videoSrc: '',
    image: '',
    order: 1,
    useCustomWidth: true,
    customWidth: 585,
  },
  content: {
    order: 2,
    bgColor: '#FFD0DB',
  },
  heading: {
    show: true,
    content: '<h2></h2><p></p>',
  },
  stats: {
    show: true,
    bgColor: '#ffffff',
    borderColor: '#FFD0DB',
    items: [{ content: '<h3></h3><p></p>' }, { content: '<h3></h3><p></p>' }, { content: '<h3></h3><p></p>' }],
  },
  pills: {
    show: true,
    bgColor: '#ffffff',
    borderColor: '#FFD0DB',
    items: [{ text: '' }, { text: '' }, { text: '' }],
  },
  featureCallout: {
    show: true,
    colorLine: '#AD4870',
    content: '<h3></h3><p></p>',
  },
  testimonial: {
    show: true,
    colorHighlight: '#FFD0DB',
    avatar: '',
    content: '<h4></h4><p></p>',
  },
  button: {},
});

export default {
  name: 'CalyrafitSideBySideSectionEditor',
  components: { UploadImage, ButtonPrimary, SectionLayout, ColorPicker, VideoPreview },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      MAX_STATS_ITEMS,
      MIN_STATS_ITEMS,
      MAX_PILL_ITEMS,
      MIN_PILL_ITEMS,
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
    };
  },
  computed: {
    mediaType: {
      get() {
        return this.fields.media.useVideo ? 'video' : 'image';
      },
      set(val) {
        this.fields.media.useVideo = val === 'video';
      },
    },
    mediaOrder: {
      get() {
        return this.fields.media.order;
      },
      set(val) {
        this.fields.media.order = val;
        this.fields.content.order = val === 1 ? 2 : 1;
      },
    },
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      media: { ...def.media, ...(extract.media || {}) },
      content: { ...def.content, ...(extract.content || {}) },
      heading: { ...def.heading, ...(extract.heading || {}) },
      stats: {
        ...def.stats,
        ...(extract.stats || {}),
        items:
          Array.isArray(extract.stats?.items) && extract.stats.items.length
            ? extract.stats.items.slice(0, MAX_STATS_ITEMS)
            : def.stats.items,
      },
      pills: {
        ...def.pills,
        ...(extract.pills || {}),
        items:
          Array.isArray(extract.pills?.items) && extract.pills.items.length
            ? extract.pills.items.slice(0, MAX_PILL_ITEMS)
            : def.pills.items,
      },
      featureCallout: { ...def.featureCallout, ...(extract.featureCallout || {}) },
      testimonial: { ...def.testimonial, ...(extract.testimonial || {}) },
      button: { ...(extract.button || {}) },
    };

    this.fields.content.order = this.fields.media.order === 1 ? 2 : 1;
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const payload = {
        ...this.fields,
        stats: {
          ...this.fields.stats,
          items: (this.fields.stats.items || []).slice(0, MAX_STATS_ITEMS),
        },
        pills: {
          ...this.fields.pills,
          items: (this.fields.pills.items || []).slice(0, MAX_PILL_ITEMS),
        },
      };
      const updateHtml = updateHtmlFromData.sectionCalyrafitSideBySide(this.sectionEditor.section.html, payload);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddStatsItem() {
      if (this.fields.stats.items.length >= MAX_STATS_ITEMS) return;
      this.fields.stats.items.push(cloneDeep(buildStatsItemDefault()));
    },
    handleRemoveStatsItem(index) {
      if (this.fields.stats.items.length <= MIN_STATS_ITEMS) return;
      this.fields.stats.items.splice(index, 1);
    },
    handleAddPillItem() {
      this.fields.pills.items.push(cloneDeep(buildPillItemDefault()));
    },
    handleRemovePillItem(index) {
      if (this.fields.pills.items.length <= MIN_PILL_ITEMS) return;
      this.fields.pills.items.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 100px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
